/**
 * views/reporting/sections/PipelineWinRate.jsx - pipeline and win rate by
 * customer.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import { Icon } from '../../../components/ui/Icon';
import { fmt } from '../../../utils/format';
import { exportCsv } from '../../../utils/exportCsv';
import { exportChartPNG } from '../../../utils/exportChartPNG';
import { CSV_BTN, winRateColor } from '../reportingHelpers';
import { CARD, TICK, TT } from '../reportingTokens';
import { SectionHeader } from '../SectionHeader';

export function renderPipelineWinRate({ yearLabel, pipelineByCustomer, pipelineRef, winRateByCustomer, wrCustRef }) {
  return (
    <>
      {/* PIPELINE + WIN RATE BY CUSTOMER */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <div>
          <SectionHeader
            title="Pipeline by Customer"
            subtitle={`Top 8 by open value · ${yearLabel}`}
            explain={{
              title: 'Pipeline by Customer',
              body: 'Bar length = total dollar value of Open bids per customer, for the selected year. Won and Lost deals are not included.',
              formula: "SUM(quoted_price)\n  WHERE status = 'Open'\n  GROUP BY customer\n  LIMIT 8",
            }}
            right={
              <>
                <button
                  onClick={() =>
                    exportCsv(
                      'pipeline_by_customer.csv',
                      pipelineByCustomer.map((d) => ({ Customer: d.name, Value: d.value }))
                    )
                  }
                  style={CSV_BTN}
                >
                  <Icon name="download" size={10} color="#666" /> CSV
                </button>
                <button onClick={() => exportChartPNG(pipelineRef, 'pipeline_by_customer')} style={CSV_BTN}>
                  <Icon name="download" size={10} color="#666" /> PNG
                </button>
              </>
            }
          />
          <div ref={pipelineRef} style={{ ...CARD, height: 360, padding: 12, overflowY: 'auto' }}>
            <div style={{ height: Math.max(280, pipelineByCustomer.length * 30) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pipelineByCustomer} layout="vertical" barSize={16} margin={{ top: 6, right: 24, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" horizontal={false} />
                  <XAxis type="number" tick={TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    interval={0}
                    tick={{ ...TICK, fontSize: 10.5, fill: '#0D0D0D', fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={170}
                  />
                  <Tooltip contentStyle={TT} formatter={(v) => [fmt(v), 'Pipeline']} cursor={{ fill: '#F4F4F4' }} />
                  <Bar dataKey="value" fill="#1B5E20" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div>
          <SectionHeader
            title="Win Rate by Customer"
            subtitle={`Sorted by win rate · ${yearLabel}`}
            explain={{
              title: 'Win Rate by Customer',
              body: "Of the deals already closed with each customer, what % was won? Open bids are not yet closed, so they're excluded. Small samples (e.g. 1 deal) can show 100%, so read the W/L count to gauge confidence.",
              formula: 'won / (won + lost) per customer · color: green ≥70%, orange 40–70%, red <40%',
            }}
            right={
              <>
                <button
                  onClick={() =>
                    exportCsv(
                      'win_rate_by_customer.csv',
                      winRateByCustomer.map((d) => ({ Customer: d.name, 'Win Rate': d.winRate, Won: d.won, Lost: d.lost }))
                    )
                  }
                  style={CSV_BTN}
                >
                  <Icon name="download" size={10} color="#666" /> CSV
                </button>
                <button onClick={() => exportChartPNG(wrCustRef, 'win_rate_by_customer')} style={CSV_BTN}>
                  <Icon name="download" size={10} color="#666" /> PNG
                </button>
              </>
            }
          />
          <div ref={wrCustRef} style={{ ...CARD, height: 360, padding: 12, overflowY: 'auto' }}>
            <div style={{ height: Math.max(280, winRateByCustomer.length * 30) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={winRateByCustomer} layout="vertical" barSize={16} margin={{ top: 6, right: 60, bottom: 4, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F4F4F4" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                  <YAxis
                    dataKey="name"
                    type="category"
                    interval={0}
                    tick={{ ...TICK, fontSize: 10.5, fill: '#0D0D0D', fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    width={170}
                  />
                  <Tooltip
                    contentStyle={TT}
                    formatter={(v, n, p) => [`${v}% (${p.payload.won}W / ${p.payload.won + p.payload.lost})`, 'Win Rate']}
                    cursor={{ fill: '#F4F4F4' }}
                  />
                  <Bar
                    dataKey="winRate"
                    radius={[0, 6, 6, 0]}
                    label={{ position: 'right', formatter: (v) => `${v}%`, fontSize: 10, fontWeight: 800, fill: '#444' }}
                  >
                    {winRateByCustomer.map((e, i) => (
                      <Cell key={i} fill={winRateColor(e.winRate)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
